import { useState, useEffect } from "react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const PASS_KEY = "ph_guidebook_pass_v1";

const readPass = () => {
  try { return JSON.parse(localStorage.getItem(PASS_KEY) || "null"); } catch { return null; }
};
const track = (name, params) => { if (typeof window.gtag === "function") window.gtag("event", name, params); };
const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Something went wrong. Please try again.";
};

const openInTab = (win, path) => {
  const url = `${BASE}${path}`;
  if (win && !win.closed) win.location.href = url;
  else window.open(url, "_blank", "noopener");
  return url;
};

export const useGuidebookGate = () => {
  const [active, setActive] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const open = async (g) => {
    const pass = readPass();
    if (!pass?.token) { setActive(g); return; }
    const win = window.open("", "_blank");
    setBusyId(g.id);
    try {
      const { data } = await axios.post(`${BASE}/api/guidebooks/unlock`, { guidebook_id: g.id, access_pass: pass.token });
      openInTab(win, data.download_url);
      track("file_download", { file_name: g.title, link_text: "guidebook_returning" });
    } catch {
      if (win) win.close();
      localStorage.removeItem(PASS_KEY);
      setActive(g);
    } finally { setBusyId(null); }
  };

  return { active, busyId, open, close: () => setActive(null) };
};

const EMPTY = { first_name: "", email: "", organization: "", consent: false, website: "" };

export const GuidebookGateModal = ({ guidebook, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [link, setLink] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending"); setError("");
    const win = window.open("", "_blank");
    try {
      const { data } = await axios.post(`${BASE}/api/guidebooks/request`, { ...form, guidebook_id: guidebook.id, source_page: window.location.pathname });
      if (!data.download_url) { if (win) win.close(); setStatus("sent"); return; }
      localStorage.setItem(PASS_KEY, JSON.stringify({ token: data.access_pass, first_name: form.first_name }));
      track("generate_lead", { form_name: "guidebook_download", guidebook_title: guidebook.title });
      setLink(openInTab(win, data.download_url));
      setStatus("sent");
    } catch (err) {
      if (win) win.close();
      setError(errText(err)); setStatus("idle");
    }
  };

  return (
    <div className="gate-overlay" onClick={onClose} data-lenis-prevent data-testid="guidebook-gate-overlay">
      <div className="gate-modal" role="dialog" aria-modal="true" aria-labelledby="gate-title" onClick={(e) => e.stopPropagation()} data-testid="guidebook-gate-modal">
        <button type="button" className="gate-close" onClick={onClose} aria-label="Close" data-testid="guidebook-gate-close">×</button>
        <span className="eyebrow">Free Guidebook</span>
        <h3 id="gate-title" className="gate-title" data-testid="guidebook-gate-title">{guidebook.title}</h3>
        {status === "sent" ? (
          <div className="gate-success" data-testid="guidebook-gate-success">
            <p><strong>You're in.</strong> Your guidebook is opening in a new tab.</p>
            {link && <a href={link} target="_blank" rel="noopener noreferrer" className="btn btn-accent" data-testid="guidebook-gate-open-link">Open guidebook →</a>}
            <p className="gate-fine">This secure link expires in 10 minutes. Next time, every guidebook opens instantly.</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="guidebook-gate-form" noValidate={false}>
            <p className="gate-lede">Tell us where to send updates and we'll unlock the full library.</p>
            <div className="form-row"><label htmlFor="gb-first">First name</label><input id="gb-first" required maxLength={80} value={form.first_name} onChange={set("first_name")} data-testid="guidebook-gate-first-name" /></div>
            <div className="form-row"><label htmlFor="gb-email">Work email</label><input id="gb-email" required type="email" value={form.email} onChange={set("email")} data-testid="guidebook-gate-email" /></div>
            <div className="form-row"><label htmlFor="gb-org">Organization <span className="gate-opt">(optional)</span></label><input id="gb-org" maxLength={160} value={form.organization} onChange={set("organization")} data-testid="guidebook-gate-organization" /></div>
            <div className="gate-hp" aria-hidden="true">
              <label htmlFor="gb-website">Website</label>
              <input id="gb-website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="guidebook-gate-honeypot" />
            </div>
            <label className="gate-consent">
              <input type="checkbox" required checked={form.consent} onChange={set("consent")} data-testid="guidebook-gate-consent" />
              <span>I agree to receive this guidebook and occasional safety updates from Players Health. See our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
            </label>
            <button type="submit" className="btn btn-accent gate-submit" disabled={status === "sending"} data-testid="guidebook-gate-submit">
              {status === "sending" ? "Unlocking…" : "Get the guidebook"}
            </button>
            {error && <p className="gate-error" role="alert" data-testid="guidebook-gate-error">{error}</p>}
          </form>
        )}
      </div>
    </div>
  );
};
